<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-bandwidth"
    title="クラウド向けQoS詳細設定"
  >
    <template v-slot:body="{ cancel }">
      <cross-validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- VPN/VNコード -->
                <v-row>
                  <v-col>
                    <app-label-group
                      label="VPN/VNコード"
                      :value="
                        vpnVnCode.charAt(0) !== 'B'
                          ? `${vpnVnCode}(${vnName})`
                          : vpnVnCode
                      "
                    />
                  </v-col>
                </v-row>

                <!-- QoS方式 -->
                <v-row>
                  <v-col>
                    <validation-provider
                      name="qosType"
                      label="QoS方式"
                      v-model="putCloudQosDetail.qosType"
                      :rules="{ required_value: { check: 'NONE' } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-badge item="QoS方式" :isInfo="false" />
                      <app-select
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                        :items="qosTypeOptions"
                        item-title="text"
                        item-value="value"
                      ></app-select>
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- 優先度参照先 -->
                <v-row class="mb-4">
                  <v-col>
                    <template v-if="putCloudQosDetail.qosType !== 'NONE'">
                      <app-label-group
                        label="優先度参照先"
                        :value="
                          $filter.enumTo(
                            putCloudQosDetail.priorityTarget,
                            'cloudQos_PriorityTarget'
                          )
                        "
                      />
                    </template>
                  </v-col>
                </v-row>

                <!-- QoS方式：絶対優先方式 -->
                <template v-if="putCloudQosDetail.qosType === 'ABSOLUTE'">
                  <h3 class="font-weight-bold">優先度割当</h3>
                  <v-row>
                    <v-col>
                      <v-table density="compact" class="table table-bordered">
                        <thead>
                          <tr>
                            <th colspan="2" class="text-center">
                              IP Precedence
                            </th>
                            <th
                              class="text-center"
                              v-for="index in 8"
                              :key="index"
                            >
                              {{ index - 1 }}
                            </th>
                          </tr>
                          <tr>
                            <th colspan="2" class="text-center">DSCP</th>
                            <th
                              v-for="(
                                colName, colNameIndex
                              ) in absolutePriorityColNames"
                              :key="colNameIndex"
                              class="text-center absolute-width"
                            >
                              {{ colName }}
                            </th>
                          </tr>
                        </thead>
                        <tbody class="text-center">
                          <th :rowspan="5">優先度</th>
                          <tr
                            v-for="(
                              rowName, rowNameIndex
                            ) in absolutePriorityRowNames"
                            :key="rowNameIndex"
                          >
                            <th class="text-left">
                              {{ rowName.value }}
                            </th>
                            <td
                              v-for="(
                                propName, propNameIndex
                              ) in absolutePriorityPropNames"
                              :key="propNameIndex"
                            >
                              {{
                                putCloudQosDetail.cloudQosAbsolute![
                                  propName as PriorityProps
                                ] === rowName.key
                                  ? "●"
                                  : "○"
                              }}
                            </td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-col>
                  </v-row>
                </template>

                <!-- QoS方式：帯域制御方式 -->
                <template v-if="putCloudQosDetail.qosType === 'BANDWIDTH'">
                  <h3 class="font-weight-bold">優先度割当</h3>
                  <v-row>
                    <v-col>
                      <v-table density="compact" class="table table-bordered">
                        <thead>
                          <tr class="text-center">
                            <th colspan="2" rowspan="2"></th>
                            <th colspan="5" class="text-center">DSCP</th>
                          </tr>
                          <tr>
                            <th
                              v-for="(
                                colName, colNameIndex
                              ) in bandwidthColNames"
                              :key="colNameIndex"
                            >
                              {{ colName }}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          <tr
                            v-for="(rowName, rowNameIndex) in bandwidthRowNames"
                            :key="rowNameIndex"
                          >
                            <template v-if="rowNameIndex % 2 === 0">
                              <th rowspan="2">
                                {{ rowName.value }}
                              </th>
                              <th>割当帯域</th>
                              <td class="select-width">
                                <app-select
                                  :items="bandwidthValueOptions"
                                  v-model="
                                    cloudQosBandwidthList[
                                      Math.floor(rowNameIndex / 2)
                                    ].dscp18Value
                                  "
                                  item-title="text"
                                  item-value="value"
                                  hide-details
                                ></app-select>
                              </td>
                              <td class="select-width">
                                <app-select
                                  :items="bandwidthValueOptions"
                                  v-model="
                                    cloudQosBandwidthList[
                                      Math.floor(rowNameIndex / 2)
                                    ].dscp24Value
                                  "
                                  item-title="text"
                                  item-value="value"
                                  hide-details
                                ></app-select>
                              </td>
                              <td class="select-width">
                                <app-select
                                  :items="bandwidthValueOptions"
                                  v-model="
                                    cloudQosBandwidthList[
                                      Math.floor(rowNameIndex / 2)
                                    ].dscp34Value
                                  "
                                  item-title="text"
                                  item-value="value"
                                  hide-details
                                >
                                </app-select>
                              </td>
                              <td class="select-width">
                                <app-select
                                  :items="bandwidthValueOptions"
                                  v-model="
                                    cloudQosBandwidthList[
                                      Math.floor(rowNameIndex / 2)
                                    ].dscp46Value
                                  "
                                  item-title="text"
                                  item-value="value"
                                  hide-details
                                ></app-select>
                              </td>
                              <td class="text-right">
                                <template
                                  v-if="
                                    sumDscpValue(
                                      cloudQosBandwidthList[
                                        Math.floor(rowNameIndex / 2)
                                      ]
                                    ) > 90
                                  "
                                >
                                  <span class="text-red">OVER</span>
                                </template>
                                <template v-else>
                                  {{
                                    calcBestEffort(
                                      cloudQosBandwidthList[
                                        Math.floor(rowNameIndex / 2)
                                      ]
                                    )
                                  }}
                                </template>
                              </td>
                            </template>
                            <template v-else>
                              <th>最優先</th>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>
                                <app-select
                                  :items="onOff"
                                  v-model="
                                    cloudQosBandwidthList[
                                      Math.floor(rowNameIndex / 2)
                                    ].isDscp46TopPriority
                                  "
                                  item-title="text"
                                  item-value="value"
                                  hide-details
                                ></app-select>
                              </td>
                              <td></td>
                            </template>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-col>
                  </v-row>
                </template>
              </v-container>
            </div>
          </v-card-text>
          <!--
              関連項目チェック
              No.1
              項目名: QoS方式 DSCP_46～10 割当帯域割合_46～18（パターンA～パターンB）
              チェック内容: QoS方式が「帯域制御方式」の場合、割当帯域割合_46～18が1つ以上設定されていること
              パターンA～パターンB全て設定される必要はなく、最低限どれか１つ設定されていれば良い。
              -->
          <cross-validation-provider
            :passed="crossValidation1"
            vid="crossValidation1"
            class="my-4"
          >
            DSCPに対する割当帯域割合を設定してください。
          </cross-validation-provider>

          <!--
              関連項目チェック
              No.2
              項目名: 割当帯域割合
              チェック内容: QoS方式が「帯域制御方式」かつ、割当帯域割合に値が設定されている場合、
              パターンごとの割当帯域割合の合計値が「合計値≦90%」であること
              -->
          <cross-validation-provider
            :passed="crossValidation2"
            vid="crossValidation2"
            class="my-4"
          >
            割当帯域割合はパターン内の合計値が90%以下になるように設定してください。
          </cross-validation-provider>

          <!--
              関連項目チェック
              No.3
              項目名: 最優先
              チェック内容: 最優先が「ON」の場合、割当帯域割合が「設定値 ≧ 5%」かつ「設定値 ＜ 50%」であること
              -->
          <cross-validation-provider
            :passed="crossValidation3"
            vid="crossValidation3"
            class="my-4"
          >
            最優先を「ON」にした場合、割当帯域割合は「5%～50%」の範囲で設定してください。
          </cross-validation-provider>

          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">設定</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type {
  CloudQosDetail,
  PutCloudQosDetail,
  CloudQosBandwidth,
} from "@/apis/CloudQosApi";
import CloudQosViewRC from "@/modals/cloudQos/CloudQosViewRC.vue";
import { VueFinalModal } from "vue-final-modal";
import cloneDeep from "lodash/cloneDeep";

type PriorityProps =
  | "priority0"
  | "priority1"
  | "priority2"
  | "priority3"
  | "priority4"
  | "priority5"
  | "priority6"
  | "priority7";

export default defineComponent({
  name: "CloudQosEdit",
  props: {
    vpnVnCode: {
      type: String as PropType<string>,
      required: true,
    },
    vnName: {
      type: String as PropType<string>,
      required: true,
    },
    isNew: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    cloudQosDetail: {
      type: Object as PropType<CloudQosDetail>,
      required: true,
    },
  },
  data() {
    return {
      putCloudQosDetail: {
        qosType: "NONE",
        priorityTarget: "NONE",
        cloudQosAbsolute: {
          priority0: "LOW",
          priority1: "LOW",
          priority2: "NORMAL",
          priority3: "NORMAL",
          priority4: "MEDIUM",
          priority5: "HIGH",
          priority6: "LOW",
          priority7: "LOW",
        },
      } as PutCloudQosDetail,
      cloudQosBandwidthList: [] as Omit<CloudQosBandwidth, "bestEffort">[],
      absolutePriorityRowNames: [
        { key: "HIGH", value: "High" },
        { key: "MEDIUM", value: "Medium" },
        { key: "NORMAL", value: "Normal" },
        { key: "LOW", value: "Low" },
      ],
      absolutePriorityColNames: ["0", "10", "18", "24", "34", "46", "-", "-"],
      absolutePriorityPropNames: [
        "priority0",
        "priority1",
        "priority2",
        "priority3",
        "priority4",
        "priority5",
        "priority6",
        "priority7",
      ] as PriorityProps[],
      bandwidthColNames: ["18", "24", "34", "46", "ベストエフォート"],
      bandwidthRowNames: [
        { key: "PATTERN_A", value: "パターンA" },
        { key: "PATTERN_A", value: "" },
        { key: "PATTERN_B", value: "パターンB" },
        { key: "PATTERN_B", value: "" },
      ],
      bandwidthValueOptions: [
        { value: null, text: "" },
        { value: 5, text: "5%" },
        { value: 10, text: "10%" },
        { value: 15, text: "15%" },
        { value: 20, text: "20%" },
        { value: 25, text: "25%" },
        { value: 30, text: "30%" },
        { value: 35, text: "35%" },
        { value: 40, text: "40%" },
        { value: 45, text: "45%" },
        { value: 50, text: "50%" },
        { value: 55, text: "55%" },
        { value: 60, text: "60%" },
        { value: 65, text: "65%" },
        { value: 70, text: "70%" },
        { value: 75, text: "75%" },
        { value: 80, text: "80%" },
        { value: 85, text: "85%" },
        { value: 90, text: "90%" },
      ],
      onOff: [
        { value: true, text: "ON" },
        { value: false, text: "OFF" },
      ],
    };
  },
  computed: {
    crossValidation1(): boolean {
      return this.putCloudQosDetail.qosType === "BANDWIDTH" &&
        this.cloudQosBandwidthList
        ? this.cloudQosBandwidthList.some(
            (e) =>
              (e.dscp18Value || 0) +
                (e.dscp24Value || 0) +
                (e.dscp34Value || 0) +
                (e.dscp46Value || 0) >
              0
          )
        : true;
    },
    crossValidation2(): boolean {
      return this.putCloudQosDetail.qosType === "BANDWIDTH" &&
        this.cloudQosBandwidthList
        ? !this.cloudQosBandwidthList.some(
            (e) =>
              (e.dscp18Value || 0) +
                (e.dscp24Value || 0) +
                (e.dscp34Value || 0) +
                (e.dscp46Value || 0) >
              90
          )
        : true;
    },
    crossValidation3(): boolean {
      return this.putCloudQosDetail.qosType === "BANDWIDTH" &&
        this.cloudQosBandwidthList
        ? !this.cloudQosBandwidthList.some(
            (e) =>
              e.isDscp46TopPriority &&
              ((e.dscp46Value || 0) < 5 || 50 < (e.dscp46Value || 0))
          )
        : true;
    },

    qosTypeOptions(): { value: string; text: string }[] {
      if (this.vpnVnCode.startsWith("C")) {
        return [
          { value: "NONE", text: "" },
          { value: "ABSOLUTE", text: "絶対優先方式" },
        ];
      } else {
        return [
          { value: "NONE", text: "" },
          { value: "ABSOLUTE", text: "絶対優先方式" },
          { value: "BANDWIDTH", text: "帯域制御方式" },
        ];
      }
    },
  },
  watch: {
    "putCloudQosDetail.qosType": function (val: string) {
      switch (val) {
        case "ABSOLUTE":
          this.putCloudQosDetail.priorityTarget = "IPPRECEDENCE_DSCP";
          break;
        case "BANDWIDTH":
          this.putCloudQosDetail.priorityTarget = "DSCP";
          break;
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.putCloudQosDetail.cloudQosKeyId = this.cloudQosDetail.cloudQosKeyId;
      this.putCloudQosDetail.qosType = this.cloudQosDetail.qosType;
      this.putCloudQosDetail.priorityTarget =
        this.cloudQosDetail.priorityTarget;

      if (this.putCloudQosDetail.qosType === "NONE") {
        this.intBandwidthList();
      }

      if (this.putCloudQosDetail.qosType === "ABSOLUTE") {
        if (this.cloudQosDetail.cloudQosAbsolute) {
          this.putCloudQosDetail.cloudQosAbsolute = {
            ...this.cloudQosDetail.cloudQosAbsolute,
          };
        }
        this.intBandwidthList();
      }
      if (this.putCloudQosDetail.qosType === "BANDWIDTH") {
        ["PATTERN_A", "PATTERN_B"].forEach((pattern) => {
          const item = this.cloudQosDetail.cloudQosBandwidthList?.find(
            (e) => e.patternName === pattern
          );
          if (item) {
            this.cloudQosBandwidthList.push({
              patternName: item.patternName as "PATTERN_A" | "PATTERN_B",
              dscp18Value: item.dscp18Value,
              dscp24Value: item.dscp24Value,
              dscp34Value: item.dscp34Value,
              dscp46Value: item.dscp46Value,
              isDscp46TopPriority: item.isDscp46TopPriority,
            });
          } else {
            this.cloudQosBandwidthList.push({
              patternName: pattern as "PATTERN_A" | "PATTERN_B",
              dscp18Value: null,
              dscp24Value: null,
              dscp34Value: null,
              dscp46Value: null,
              isDscp46TopPriority: false,
            });
          }
        });
      }
    },
    intBandwidthList() {
      ["PATTERN_A", "PATTERN_B"].forEach((pattern) => {
        this.cloudQosBandwidthList.push({
          patternName: pattern as "PATTERN_A" | "PATTERN_B",
          dscp18Value: null,
          dscp24Value: null,
          dscp34Value: null,
          dscp46Value: null,
          isDscp46TopPriority: false,
        });
      });
    },
    /**
     * 帯域制御方式の優先タイプ値の合計を算出
     * @param item クラウドQoS帯域設定地情報
     */
    sumDscpValue(item: Omit<CloudQosBandwidth, "bestEffort">): number {
      return (
        (item.dscp18Value || 0) +
        (item.dscp24Value || 0) +
        (item.dscp34Value || 0) +
        (item.dscp46Value || 0)
      );
    },
    /**
     * 帯域制御方式のベストエフォートを算出
     * @param item クラウドQoS帯域設定地情報
     */
    calcBestEffort(item: Omit<CloudQosBandwidth, "bestEffort">): string {
      return (item.dscp18Value || 0) +
        (item.dscp24Value || 0) +
        (item.dscp34Value || 0) +
        (item.dscp46Value || 0) >
        0
        ? `${
            100 -
            ((item.dscp18Value || 0) +
              (item.dscp24Value || 0) +
              (item.dscp34Value || 0) +
              (item.dscp46Value || 0))
          }%`
        : "-";
    },
    async submit() {
      // バックへ投入するデータを用意
      const putDetail = cloneDeep(this.putCloudQosDetail);
      if (putDetail.qosType === "ABSOLUTE") {
        putDetail.cloudQosBandwidthList = [];
      } else if (putDetail.qosType === "BANDWIDTH") {
        putDetail.cloudQosAbsolute = null;
        putDetail.cloudQosBandwidthList = this.cloudQosBandwidthList;
      } else {
        // ないはずけど、念の為ガードする
        throw new Error(`unknown qosType. ${putDetail.qosType}`);
      }

      await this.$modal.show(CloudQosViewRC, {
        mode: "C",
        isNew: this.isNew,
        cloudQosDetail: putDetail,
      });

      await this.$api.cloudQosApi.putCloudQosDetail(this.vpnVnCode, putDetail);

      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>

<style lang="scss" scoped>
.v-table__wrapper > table > thead > tr > th {
  border: 1px solid #dee2e6;
}
.v-table__wrapper > table > tbody > tr:not(:last-child) > th {
  border: 1px solid #dee2e6;
}
.table {
  border: 1px solid #dee2e6;
  th {
    background-color: #e9ecef;
    font-weight: bold !important;
    padding: 4px !important;
  }
}
:deep(td) {
  padding: 4px !important;
}
.select-width {
  width: 104px;
}
.absolute-width {
  width: 50px;
}
</style>

<style lang="scss">
.modal-bandwidth {
  width: 770px;

  .modal-body {
    max-height: 70vh;
    overflow: auto;
  }
}
</style>
